import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  ActivityIndicator,
  type GestureResponderEvent,
  ScrollView,
  type TextInput,
  View,
} from 'react-native'
import {msg} from '@lingui/core/macro'
import {Trans} from '@lingui/react/macro'
import {useLingui} from '@lingui/react'
import {useNavigation} from '@react-navigation/native'
import {useQueryClient} from '@tanstack/react-query'

import {HITSLOP_10, HITSLOP_20} from '#/lib/constants'
import {type NavigationProp} from '#/lib/routes/types'
import {isWeb} from '#/platform/detection'
import {usePreferencesQuery} from '#/state/queries/preferences'
import {
  unstableCacheProfileView,
  useProfilesQuery,
} from '#/state/queries/profile'
import {useSession} from '#/state/session'
import {SearchHistory} from '#/screens/Search/components/SearchHistory'
import {Explore} from '#/screens/Search/Explore'
import {SearchResults} from '#/screens/Search/SearchResults'
import {atoms as a, useBreakpoints, useTheme, web} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {SearchInput} from '#/components/forms/SearchInput'
import * as Layout from '#/components/Layout'
import {Text} from '#/components/Typography'
import {account, useStorage} from '#/storage'
import type * as bsky from '#/types/bsky'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {CarouselTrendsSection} from './components/CarouselTrendsSection'
import {CompactTrendCard} from './components/CompactTrendCard'
import {FeaturedCardsCarousel} from './components/FeaturedCardsCarousel'
import {FeaturedTrendCard} from './components/FeaturedTrendCard'
import {HeroTrendCard} from './components/HeroTrendCard'
import {RegionPicker} from './components/RegionPicker'
import {TopTrendsSection} from './components/TopTrendsSection'
import {TrendComposerPrompt} from './components/TrendComposerPrompt'
import {
  type TrendsCategory,
  TrendsCategoryChips,
} from './components/TrendsCategoryChips'
import {TrendSearchHeader} from './components/TrendSearchHeader'
import {type TrendsScope, TrendsScopeTabs} from './components/TrendsScopeTabs'
import {getDeviceDefaultRegionCode, getRegionByCode} from './data/regions'
import {
  filterTrendsResponseByCategory,
  findMatchingTrend,
  type TrendSection,
  type TrendsResponse,
  withInterestsHighlights,
} from './data/trends'
import {useTrends} from './data/useTrends'

/**
 * W Social's custom `/search` screen.
 *
 * Replaces upstream `SearchScreenShell` (`src/screens/Search/Shell.tsx`) so we
 * can drift freely from the Bluesky search/explore behaviour without creating
 * merge conflicts on upstream files. Composed from reusable primitives
 * (`Layout.*`, `SearchInput`, upstream `SearchResults`). **Never** edit
 * `src/screens/Search/Shell.tsx` to extend this screen — add or rewire
 * pieces here instead.
 *
 * Two display modes:
 *
 *  - **Explore** (no query): sticky search header + scope tabs + category
 *    chips, then a scrollable trends list driven by mock data.
 *
 *  - **Search** (query present): sticky search header. If the query
 *    matches a known trend, render `TrendSearchHeader` +
 *    `FeaturedCardsCarousel` above the results. Then upstream's
 *    `SearchResults` Pager (Top / Latest / People / Feeds) takes over.
 *
 * See `.agents/repos/root/trends/trends-how-it-works.md` — the trend detail
 * is intentionally a search-result enrichment, not a separate screen.
 */
export function WSearchScreen({queryParam}: {queryParam: string}) {
  const t = useTheme()
  const {_} = useLingui()
  const {gtMobile} = useBreakpoints()
  const navigation = useNavigation<NavigationProp>()
  const textInput = useRef<TextInput>(null)

  const [searchText, setSearchText] = useState<string>(queryParam)
  const [trendsScope, setTrendsScope] = useState<TrendsScope>('global')
  // Lazy initialiser: seed the region picker from the device/browser locale
  // (e.g. Belgium → BE), falling back to Europe. Runs once on mount.
  const [trendsRegion, setTrendsRegion] = useState<string>(
    getDeviceDefaultRegionCode,
  )
  const [trendsCategory, setTrendsCategory] = useState<TrendsCategory | null>(
    null,
  )
  const [activeResultsTab, setActiveResultsTab] = useState(0)

  // Recent searches: shown when the input is focused and empty (mirrors
  // upstream Shell, which WSearchScreen replaces). Same account-scoped storage
  // keys as Shell, so history stays consistent across the app.
  const queryClient = useQueryClient()
  const {currentAccount} = useSession()
  const [searchFocused, setSearchFocused] = useState(false)
  const [termHistory = [], setTermHistory] = useStorage(account, [
    currentAccount?.did ?? 'pwi',
    'searchTermHistory',
  ] as const)
  const [accountHistory = [], setAccountHistory] = useStorage(account, [
    currentAccount?.did ?? 'pwi',
    'searchAccountHistory',
  ])
  const {data: accountHistoryProfiles} = useProfilesQuery({
    handles: accountHistory,
    maintainData: true,
  })

  const updateSearchHistory = useCallback(
    (item: string) => {
      if (!item) return
      setTermHistory([item, ...termHistory.filter(s => s !== item)].slice(0, 6))
    },
    [termHistory, setTermHistory],
  )
  const deleteSearchHistoryItem = useCallback(
    (item: string) => {
      setTermHistory(termHistory.filter(s => s !== item))
    },
    [termHistory, setTermHistory],
  )
  const onHistoryProfileClick = useCallback(
    (profile: bsky.profile.AnyProfileView) => {
      // The SearchHistory rows navigate via their own <Link>; we just cache the
      // profile and bump it to the top of history (delayed to avoid updating
      // during the push-nav animation, matching Shell).
      unstableCacheProfileView(queryClient, profile)
      setSearchFocused(false)
      setTimeout(() => {
        setAccountHistory(
          [profile.did, ...accountHistory.filter(d => d !== profile.did)].slice(
            0,
            10,
          ),
        )
      }, 400)
    },
    [queryClient, accountHistory, setAccountHistory],
  )
  const deleteProfileHistoryItem = useCallback(
    (profile: bsky.profile.AnyProfileView) => {
      setAccountHistory(accountHistory.filter(d => d !== profile.did))
    },
    [accountHistory, setAccountHistory],
  )

  // Keep the input in sync with the route's q param. Fires on external
  // navigations (a card tap calling setParams, browser back/forward on web,
  // deep links). User typing only updates `searchText` locally and doesn't
  // trip this effect.
  useEffect(() => {
    setSearchText(queryParam)
  }, [queryParam])

  // One fetch per scope/region — never by category. The category chip filters
  // this response client-side (see `filterTrendsResponseByCategory`).
  const {
    data: regionResponse,
    isLoading: trendsLoading,
    isError: trendsError,
    isOffline: trendsOffline,
  } = useTrends({
    scope: trendsScope,
    region: trendsRegion,
  })

  // The viewer's selected interests. Categories are aligned 1:1 with the
  // interest taxonomy, so under "All" we personalise the feed to these tags.
  const {data: preferences} = usePreferencesQuery()
  const interestTags = preferences?.interests?.tags

  // Categories are derived from those actually present in the current
  // scope/region response, in first-appearance order.
  const availableCategories = useMemo(() => {
    const seen = new Set<TrendsCategory>()
    const ordered: TrendsCategory[] = []
    for (const section of regionResponse?.sections ?? []) {
      for (const trend of section.trends) {
        if (!seen.has(trend.category)) {
          seen.add(trend.category)
          ordered.push(trend.category)
        }
      }
    }
    return ordered
  }, [regionResponse])

  // Safety net: if a scope switch yields a set that no longer has the selected
  // category, fall back to "All". (Region switches reset to All explicitly in
  // `onSelectRegion`.) Guarded on a loaded response so it doesn't fire during
  // the fetch gap when the category list is momentarily empty.
  useEffect(() => {
    if (
      regionResponse &&
      trendsCategory &&
      !availableCategories.includes(trendsCategory)
    ) {
      setTrendsCategory(null)
    }
  }, [regionResponse, availableCategories, trendsCategory])

  // Apply the chip client-side for display:
  //  - a specific category → every trend in that category;
  //  - "All" (no category) → drop the backend's "Trending topics" block and
  //    replace it with a "Your interests" carousel aggregating every trend in
  //    the category sections that matches the viewer's interests (skipped when
  //    none match). The category sections themselves are unchanged.
  const trendsResponse = useMemo(() => {
    if (!regionResponse) return undefined
    return trendsCategory
      ? filterTrendsResponseByCategory(regionResponse, trendsCategory)
      : withInterestsHighlights(
          regionResponse,
          interestTags ?? [],
          _(msg`Your interests`),
        )
  }, [regionResponse, trendsCategory, interestTags, _])

  // Match against the full (unfiltered) response so trend detail still resolves
  // even when a category chip would have hidden the trend.
  const matchedTrend = useMemo(
    () =>
      regionResponse ? findMatchingTrend(queryParam, regionResponse) : null,
    [queryParam, regionResponse],
  )

  // Web: position the sticky tab bar of the embedded SearchResults Pager
  // directly under our sticky search header.
  const [headerHeight, setHeaderHeight] = useState(0)
  const headerRef = useRef<View>(null)

  const showMobileHeader = !gtMobile
  const inQueryMode = queryParam.length > 0
  /*
   * Show recent searches whenever the input is focused — over whatever body
   * would otherwise render (Explore feed or results).
   *
   * Deliberately not gated on `searchText` being empty. Results only refresh on
   * submit (`onSubmit` -> setParams), so while the user edits an existing query
   * the previous query's results would otherwise stay on screen — including its
   * "No results found for <old query>" message, which reads as the search being
   * broken. Covering them matches upstream Shell, which hides the results body
   * whenever its autocomplete layer is open.
   */
  const showHistory = searchFocused

  const onSubmit = useCallback(() => {
    const q = searchText.trim()
    if (!q) return
    updateSearchHistory(q)
    setSearchFocused(false)
    navigation.setParams({q})
    if (!isWeb) {
      textInput.current?.blur()
    }
  }, [navigation, searchText, updateSearchHistory])

  // Tapping a recent search runs it (and re-bumps it to the top of history).
  const onHistoryItemClick = useCallback(
    (item: string) => {
      setSearchText(item)
      updateSearchHistory(item)
      setSearchFocused(false)
      navigation.setParams({q: item})
      textInput.current?.blur()
    },
    [navigation, updateSearchHistory],
  )

  const onClearText = useCallback(() => {
    setSearchText('')
    textInput.current?.focus()
  }, [])

  const onCancelSearch = useCallback(() => {
    // Clear with '' (not undefined) — `setParams({q: undefined})` serializes to
    // a literal `?q=undefined` in the web URL, which reads back as the string
    // "undefined" on reload. Matches upstream Shell's clear behavior.
    navigation.setParams({q: ''})
    setSearchText('')
    setSearchFocused(false)
    textInput.current?.blur()
  }, [navigation])

  // Picking a region from the always-visible header picker activates the
  // Regional scope AND returns to the Explore feed. Clearing the query matters
  // on trend detail: otherwise the new region's trends wouldn't contain the
  // open trend, so `matchedTrend` would flip to null mid-fetch and the
  // detail/search header would thrash. Going to the region's feed is also the
  // intuitive result of picking a country.
  const onSelectRegion = useCallback(
    (code: string) => {
      setTrendsRegion(code)
      setTrendsScope('region')
      // A new region gets a fresh category list — reset the chip to "All".
      setTrendsCategory(null)
      navigation.setParams({q: ''})
      setSearchText('')
      setSearchFocused(false)
    },
    [navigation],
  )

  // The "For you" tab renders Bluesky's upstream Explore feed; its module
  // search buttons focus our search input.
  const focusSearchInput = useCallback(() => {
    textInput.current?.focus()
  }, [])

  const isForYou = trendsScope === 'foryou'

  // Trend detail = a query that matches a known trend. It reuses the Explore
  // header (heading + region picker) and adds a back button, so it reads as a
  // detail page rather than a search. The Explore heading + region picker row
  // shows in every mode (explore, plain search, trend detail).
  const isTrendDetail = inQueryMode && !!matchedTrend

  // Back from trend detail returns to the Explore feed (clears the query)
  // rather than navigating away — preventDefault stops the BackButton's own
  // navigation.goBack().
  const onBackPress = useCallback(
    (evt: GestureResponderEvent) => {
      evt.preventDefault()
      onCancelSearch()
    },
    [onCancelSearch],
  )

  // The Regional tab shows the selected country's name (default Sweden) rather
  // than the generic "Regional" — see TrendsScopeTabs.
  const regionLabel = getRegionByCode(trendsRegion)?.name

  return (
    <Layout.Screen testID="wSearchScreen">
      <View
        ref={headerRef}
        onLayout={evt => {
          if (isWeb) setHeaderHeight(evt.nativeEvent.layout.height)
        }}
        style={[a.relative, a.z_10, web({position: 'sticky', top: 0})]}>
        <Layout.Center style={t.atoms.bg}>
          {showMobileHeader ? (
            <Layout.Header.Outer noBottomBorder>
              {/* Trend detail swaps the menu for a back button to the Explore
                  feed. */}
              {isTrendDetail ? (
                <Layout.Header.BackButton onPress={onBackPress} />
              ) : (
                <Layout.Header.MenuButton />
              )}
              <Layout.Header.Content align="left">
                <Layout.Header.TitleText>
                  <Trans>Trends</Trans>
                </Layout.Header.TitleText>
              </Layout.Header.Content>
              {/* Region picker pinned far right, in every mode. */}
              <RegionPicker
                selectedCode={trendsRegion}
                onSelect={onSelectRegion}
              />
            </Layout.Header.Outer>
          ) : (
            // Desktop: an Explore heading row above the search bar, with the
            // region picker pinned to the far right. On trend detail a back
            // button sits to the left of "Explore".
            <View
              style={[
                a.px_lg,
                a.pt_md,
                a.flex_row,
                a.align_center,
                a.justify_between,
                a.gap_sm,
              ]}>
              <View style={[a.flex_row, a.align_center, a.gap_xs]}>
                {isTrendDetail && (
                  <Layout.Header.BackButton onPress={onBackPress} />
                )}
                <Text style={[a.text_2xl, a.font_bold, t.atoms.text]}>
                  <Trans>Trends</Trans>
                </Text>
              </View>
              <RegionPicker
                selectedCode={trendsRegion}
                onSelect={onSelectRegion}
              />
            </View>
          )}

          <View
            style={[
              a.px_lg,
              a.pt_sm,
              a.pb_sm,
              a.flex_row,
              a.align_stretch,
              a.gap_xs,
            ]}>
            <View style={[a.flex_1]}>
              <SearchInput
                ref={textInput}
                value={searchText}
                onFocus={() => setSearchFocused(true)}
                onChangeText={setSearchText}
                onClearText={onClearText}
                onSubmitEditing={onSubmit}
                placeholder={_(msg`Search W Social`)}
                hitSlop={{...HITSLOP_20, top: 0}}
              />
            </View>
            {(inQueryMode || searchText.length > 0 || searchFocused) && (
              <Button
                label={_(msg`Cancel search`)}
                size="large"
                variant="ghost"
                color="secondary"
                shape="rectangular"
                style={[a.px_sm]}
                onPress={onCancelSearch}
                hitSlop={HITSLOP_10}>
                <ButtonText>
                  <Trans>Cancel</Trans>
                </ButtonText>
              </Button>
            )}
          </View>

          {/* Scope tabs + category chips show only in Explore mode — and are
              hidden while recent searches are open, so the history sits
              directly under the search bar (like a search-term page). */}
          {!inQueryMode && !showHistory && (
            <>
              <TrendsScopeTabs
                selected={trendsScope}
                onSelect={setTrendsScope}
                regionLabel={regionLabel}
              />
              {/* Category chips filter W trends — not shown on the For you tab,
                  which renders Bluesky's Explore feed instead. */}
              {!isForYou && (
                <TrendsCategoryChips
                  selected={trendsCategory}
                  onSelect={setTrendsCategory}
                  categories={availableCategories}
                  interests={interestTags ?? []}
                />
              )}
            </>
          )}
        </Layout.Center>
      </View>

      {showHistory ? (
        <SearchHistory
          searchHistory={termHistory}
          selectedProfiles={accountHistoryProfiles?.profiles || []}
          onItemClick={onHistoryItemClick}
          onProfileClick={onHistoryProfileClick}
          onRemoveItemClick={deleteSearchHistoryItem}
          onRemoveProfileClick={deleteProfileHistoryItem}
        />
      ) : inQueryMode ? (
        <SearchModeBody
          queryParam={queryParam}
          headerHeight={headerHeight}
          activeTab={activeResultsTab}
          onPageSelected={setActiveResultsTab}
          matchedTrendId={matchedTrend?.id}
          // Use the full (unfiltered) response so the matched trend resolves
          // even if the active category chip would have hidden it.
          trendsResponse={regionResponse}
          onCancelSearch={onCancelSearch}
        />
      ) : isForYou ? (
        // "For you" = Bluesky's upstream Explore feed, minus its trending-
        // topics block (W Social surfaces trends on the Global/Regional tabs).
        <Explore
          focusSearchInput={focusSearchInput}
          headerHeight={headerHeight}
          hideTrendingTopics
        />
      ) : (
        <ExploreModeBody
          trendsCategory={trendsCategory}
          sections={trendsResponse?.sections ?? []}
          scope={trendsScope}
          regionName={regionLabel}
          isLoading={trendsLoading}
          isError={trendsError}
          isOffline={trendsOffline}
        />
      )}
    </Layout.Screen>
  )
}

/* ---------- explore mode ---------- */

function ExploreModeBody({
  trendsCategory,
  sections,
  scope,
  regionName,
  isLoading,
  isError,
  isOffline,
}: {
  trendsCategory: TrendsCategory | null
  sections: TrendSection[]
  scope: TrendsScope
  regionName?: string
  isLoading: boolean
  isError: boolean
  isOffline: boolean
}) {
  return (
    <ScrollView contentContainerStyle={{paddingBottom: 80}}>
      <Layout.Center>
        {isLoading ? (
          <LoadingState />
        ) : sections.length > 0 ? (
          // Have data (possibly stale/cached) — show it, even while offline.
          sections.map((section, i) => (
            <TrendSectionView
              // Index-based: several sections can share the same type with no
              // title (e.g. consecutive `main`/feed sections), so type+title
              // is not unique and caused duplicate-key render bugs.
              key={`${section.type}-${section.cardStyle}-${i}`}
              section={section}
              scope={scope}
              regionName={regionName}
              isFirst={i === 0}
              isLast={i === sections.length - 1}
            />
          ))
        ) : isOffline ? (
          <OfflineState />
        ) : isError ? (
          <ErrorState />
        ) : (
          <EmptyState category={trendsCategory} />
        )}
      </Layout.Center>
    </ScrollView>
  )
}

function LoadingState() {
  return (
    <View style={[a.px_lg, a.py_4xl, a.align_center]}>
      <ActivityIndicator />
    </View>
  )
}

function ErrorState() {
  const t = useTheme()
  return (
    <View style={[a.px_lg, a.py_4xl, a.align_center, a.gap_sm]}>
      <Text style={[a.text_md, a.font_bold, t.atoms.text]}>
        <Trans>Couldn't load trends</Trans>
      </Text>
      <Text style={[a.text_sm, a.text_center, t.atoms.text_contrast_medium]}>
        <Trans>
          The trends service is unavailable right now. Please try again later.
        </Trans>
      </Text>
    </View>
  )
}

function OfflineState() {
  const t = useTheme()
  return (
    <View style={[a.px_lg, a.py_4xl, a.align_center, a.gap_sm]}>
      <Text style={[a.text_md, a.font_bold, t.atoms.text]}>
        <Trans>You're offline</Trans>
      </Text>
      <Text style={[a.text_sm, a.text_center, t.atoms.text_contrast_medium]}>
        <Trans>
          Trends will load once you're back online. Check your connection and
          try again.
        </Trans>
      </Text>
    </View>
  )
}

function TrendSectionView({
  section,
  scope,
  regionName,
  isFirst,
  isLast,
}: {
  section: TrendSection
  scope: TrendsScope
  regionName?: string
  isFirst: boolean
  isLast: boolean
}) {
  // The server decides composition: `layout` picks the container, `cardStyle`
  // picks the card for every trend in the section. The client only renders what
  // it receives — see `trends-how-it-works.md`.
  //
  // Section separation is owned HERE, not by the cards or section bodies, so the
  // dividers are order-independent — any section type following any other gets
  // the same single rule between them. Cards/sections must NOT draw their own
  // section border.
  //
  // - top border on every section EXCEPT the first: the category-chips row
  //   above already has a `border_b`, so a top border on the first section
  //   would double it.
  // - bottom border only on the last section, to close the list.
  const ss = useSemanticTokens()

  let body
  if (section.type === 'top') {
    // The ranked block ignores `layout`/`cardStyle` — cards are chosen by rank.
    body = (
      <TopTrendsSection
        section={section}
        scope={scope}
        regionName={regionName}
      />
    )
  } else {
    switch (section.layout) {
      case 'carousel':
        body = <CarouselTrendsSection section={section} />
        break
      case 'single':
      case 'feed':
      default:
        body = <FeedSectionView section={section} />
    }
  }

  return (
    <View
      style={[
        a.w_full,
        !isFirst && a.border_t,
        isLast && a.border_b,
        {borderColor: ss.border.contrast_low},
      ]}>
      {body}
    </View>
  )
}

function FeedSectionView({section}: {section: TrendSection}) {
  const t = useTheme()
  const ss = useSemanticTokens()
  return (
    <View style={[a.w_full]}>
      {section.title && (
        <View style={[a.px_lg, a.pt_lg, a.pb_sm]}>
          <Text style={[a.text_lg, a.font_bold, t.atoms.text]}>
            {section.title}
          </Text>
        </View>
      )}
      {section.trends.map((trend, i) => (
        <View
          key={trend.id}
          // Inter-card divider for multi-card feeds. The section's own top
          // divider (TrendSectionView) covers the first card, so only cards
          // after it get a separator.
          style={
            i > 0
              ? [a.border_t, {borderColor: ss.border.contrast_low}]
              : undefined
          }>
          {section.cardStyle === 'hero' ? (
            <HeroTrendCard trend={trend} />
          ) : section.cardStyle === 'featured' ? (
            <FeaturedTrendCard trend={trend} />
          ) : (
            <CompactTrendCard trend={trend} />
          )}
        </View>
      ))}
    </View>
  )
}

function EmptyState({category}: {category: TrendsCategory | null}) {
  const t = useTheme()
  return (
    <View style={[a.px_lg, a.py_4xl, a.align_center, a.gap_sm]}>
      <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
        {category ? (
          <Trans>No trends in this category right now.</Trans>
        ) : (
          <Trans>No trends to show.</Trans>
        )}
      </Text>
    </View>
  )
}

/* ---------- search mode ---------- */

function SearchModeBody({
  queryParam,
  headerHeight,
  activeTab,
  onPageSelected,
  matchedTrendId,
  trendsResponse,
  onCancelSearch: _onCancelSearch,
}: {
  queryParam: string
  headerHeight: number
  activeTab: number
  onPageSelected: (i: number) => void
  matchedTrendId: string | undefined
  trendsResponse: TrendsResponse | undefined
  onCancelSearch: () => void
}) {
  const matchedTrend = useMemo(() => {
    if (!matchedTrendId || !trendsResponse) return null
    for (const section of trendsResponse.sections) {
      for (const trend of section.trends) {
        if (trend.id === matchedTrendId) return trend
      }
    }
    return null
  }, [matchedTrendId, trendsResponse])

  // Trend detail block: document scroll handles vertical scrolling for the
  // whole page, so we render the trend as a normal block above SearchResults.
  const trendBlock = useMemo(() => {
    if (!matchedTrend) return null
    return (
      <Layout.Center>
        <TrendSearchHeader trend={matchedTrend} />
        {matchedTrend.featuredCards.length > 0 && (
          <FeaturedCardsCarousel
            cards={matchedTrend.featuredCards}
            trend={matchedTrend}
          />
        )}
        <TrendComposerPrompt trend={matchedTrend} />
      </Layout.Center>
    )
  }, [matchedTrend])

  return (
    <View style={[a.flex_1]}>
      {trendBlock}
      <SearchResults
        query={queryParam}
        queryWithParams={queryParam}
        filters={{}}
        hasFilters={false}
        activeTab={activeTab}
        onPageSelected={onPageSelected}
        headerHeight={headerHeight}
        initialPage={activeTab}
        // The trend detail (header, articles, composer) renders above the
        // results pager — switching Top/Latest/etc. must not scroll it away.
        disableScrollOnSelect
      />
    </View>
  )
}
